import React, { useState, useEffect } from 'react';
import analytics from '../utils/analytics';
import HistoricalAnalytics from './HistoricalAnalytics';

const AnalyticsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('current');

  useEffect(() => {
    // Update stats every 5 seconds
    const updateStats = () => {
      const currentStats = analytics.getDetailedStats();
      setStats(currentStats);
    };

    updateStats();
    const interval = setInterval(updateStats, 5000);

    return () => clearInterval(interval);
  }, []);

  if (!stats && activeTab === 'current') {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <div className="animate-pulse">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen">
      {/* Header with Tabs */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Analytics Dashboard</h1>
        
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('current')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'current'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🎯 Current Session
            </button>
            <button
              onClick={() => setActiveTab('historical')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'historical'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 All Sessions (Historical)
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'historical' ? (
        <HistoricalAnalytics />
      ) : (
        <CurrentSessionView stats={stats} showDetails={showDetails} setShowDetails={setShowDetails} />
      )}
    </div>
  );
};

const CurrentSessionView = ({ stats, showDetails, setShowDetails }) => {
  if (!stats) return null;

  const { userProfile, sessionStats, recentEvents } = stats;

  return (
    <div>
      <div className="mb-6">
        <p className="text-gray-600">Real-time insights for the current visitor session</p>
      </div>

      {/* Current User Profile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4 text-blue-600">Current Visitor Profile</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="font-medium">User ID:</span>
              <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {userProfile.userId.substring(0, 8)}...
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Session Duration:</span>
              <span>{Math.floor(userProfile.sessionDuration / 1000 / 60)} minutes</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Device Type:</span>
              <span className="capitalize">{userProfile.deviceInfo?.deviceType}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Browser:</span>
              <span>{userProfile.deviceInfo?.browser?.browser} {userProfile.deviceInfo?.browser?.version}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Operating System:</span>
              <span>{userProfile.deviceInfo?.os?.os} {userProfile.deviceInfo?.os?.version}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Location:</span>
              <span>
                {userProfile.locationInfo?.city}, {userProfile.locationInfo?.region}
                <br />
                <span className="text-sm text-gray-500">{userProfile.locationInfo?.country}</span>
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">ISP:</span>
              <span className="text-sm">{userProfile.locationInfo?.isp}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">First Visit:</span>
              <span>
                {userProfile.firstVisit ? 
                  new Date(parseInt(userProfile.firstVisit)).toLocaleDateString() : 
                  'Today'
                }
              </span>
            </div>
          </div>
        </div>

        {/* Session Statistics */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4 text-green-600">Current Session Statistics</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{sessionStats.pageViews}</div>
              <div className="text-sm text-gray-600">Page Views</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{sessionStats.totalClicks}</div>
              <div className="text-sm text-gray-600">Total Clicks</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{sessionStats.maxScrollDepth}%</div>
              <div className="text-sm text-gray-600">Max Scroll</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{sessionStats.eventsCount}</div>
              <div className="text-sm text-gray-600">Total Events</div>
            </div>
          </div>
        </div>
      </div>

      {/* Device Information */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 text-purple-600">Device Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <h3 className="font-semibold mb-2">Screen Resolution</h3>
            <p className="text-sm text-gray-600">
              {userProfile.deviceInfo?.screen?.width} × {userProfile.deviceInfo?.screen?.height}
            </p>
            <p className="text-xs text-gray-500">
              Viewport: {userProfile.deviceInfo?.viewport?.width} × {userProfile.deviceInfo?.viewport?.height}
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Platform</h3>
            <p className="text-sm text-gray-600">{userProfile.deviceInfo?.platform}</p>
            <p className="text-xs text-gray-500">
              Color Depth: {userProfile.deviceInfo?.screen?.colorDepth}-bit
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Language</h3>
            <p className="text-sm text-gray-600">{userProfile.deviceInfo?.language}</p>
            <p className="text-xs text-gray-500">
              {userProfile.deviceInfo?.languages?.join(', ')}
            </p>
          </div>
          {userProfile.deviceInfo?.connection && (
            <div>
              <h3 className="font-semibold mb-2">Connection</h3>
              <p className="text-sm text-gray-600">
                {userProfile.deviceInfo.connection.effectiveType?.toUpperCase()}
              </p>
              <p className="text-xs text-gray-500">
                {userProfile.deviceInfo.connection.downlink} Mbps
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-orange-600">Recent Activity (This Session)</h2>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {recentEvents.map((event, index) => (
            <div key={index} className="border-l-4 border-blue-200 pl-4 py-2">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium text-gray-900">{event.event}</div>
                  <div className="text-sm text-gray-600">
                    {new Date(event.timestamp).toLocaleTimeString()}
                  </div>
                  {showDetails && (
                    <div className="mt-2 text-xs text-gray-500">
                      <pre className="bg-gray-50 p-2 rounded overflow-x-auto">
                        {JSON.stringify(event, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-400">{event.path}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Debug Information */}
      <div className="mt-8 p-4 bg-gray-100 rounded-lg">
        <details>
          <summary className="cursor-pointer font-medium text-gray-700">
            Debug Information (Click to expand)
          </summary>
          <pre className="mt-4 text-xs bg-white p-4 rounded border overflow-x-auto">
            {JSON.stringify(userProfile, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
};

export default AnalyticsDashboard; 